import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, Facebook, Instagram, Youtube, Twitter } from 'lucide-react';
import { siteData } from '../data/mock';

const ContactPage = () => {
  const { contact } = siteData;
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    service: '',
    message: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Create email content
    const emailSubject = formData.subject || `Contact Form: ${formData.service || 'General Inquiry'}`;
    const emailBody = `
Name: ${formData.name}
Email: ${formData.email}
Service Interest: ${formData.service || 'Not specified'}
Subject: ${formData.subject || 'Not specified'}

Message:
${formData.message}

---
Sent from romerosumpter.com contact form
    `.trim();

    // Create mailto link
    const mailtoLink = `mailto:romero.sumpter@gmail.com?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
    
    // Open email client
    window.location.href = mailtoLink;
    
    // Show confirmation
    alert('Thank you for your message! Your email client will open to send the message.');
    
    // Reset form
    setFormData({
      name: '',
      email: '',
      subject: '',
      service: '',
      message: ''
    });
  };

  return (
    <div className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-gray-900 to-black">
        <div className="container mx-auto px-4 text-center">
          <h1 className="heading-1 mb-6">Get In Touch</h1>
          <p className="body-large text-lime-400 mb-4">Let's Connect</p>
          <p className="body-medium text-gray-300 max-w-2xl mx-auto">
            Whether you're seeking spiritual guidance, interested in my books, or ready for custom tailoring, I'm here to serve you with excellence.
          </p>
        </div>
      </section>

      {/* Contact Form & Info Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div>
              <h2 className="heading-2 mb-8">Send a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block body-small text-gray-300 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-lime-400 transition-colors"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block body-small text-gray-300 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-lime-400 transition-colors"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="service" className="block body-small text-gray-300 mb-2">
                      Service Interest
                    </label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-lime-400 transition-colors"
                    >
                      <option value="">Select a service</option>
                      <option value="ministry">Ministry</option>
                      <option value="books">Books</option>
                      <option value="clothier">Custom Clothier</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="subject" className="block body-small text-gray-300 mb-2">
                      Subject
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-lime-400 transition-colors"
                      placeholder="Brief subject"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block body-small text-gray-300 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-lime-400 transition-colors resize-vertical"
                    placeholder="Please share your message, questions, or requirements..."
                  />
                </div>

                <button type="submit" className="btn-primary w-full">
                  Send Message <Send size={18} className="ml-2" />
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div>
              <h2 className="heading-2 mb-8">Contact Information</h2>
              
              <div className="space-y-8">
                {/* Email */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail size={20} className="text-black" />
                  </div>
                  <div>
                    <h3 className="heading-3 mb-2">Email</h3>
                    <a 
                      href={`mailto:${contact.email}`} 
                      className="body-small text-gray-300 hover:text-yellow-400 transition-colors"
                    >
                      {contact.email}
                    </a>
                  </div>
                </div>

                {/* Availability */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0">
                    <Clock size={20} className="text-black" />
                  </div>
                  <div>
                    <h3 className="heading-3 mb-2">Availability</h3>
                    <p className="body-small text-gray-300">
                      {contact.businessHours.general}
                    </p>
                    <p className="body-small text-yellow-400 mt-2">
                      Email: {contact.email}
                    </p>
                  </div>
                </div>
              </div>

              {/* Social Media */}
              <div className="mt-12">
                <h3 className="heading-3 mb-6">Follow Me</h3>
                <div className="flex space-x-4">
                  <a 
                    href={contact.social.facebook} 
                    className="w-12 h-12 bg-gray-800 rounded-full flex items-center justify-center hover:bg-lime-400 hover:text-black transition-colors"
                    aria-label="Facebook"
                  >
                    <Facebook size={20} />
                  </a>
                  <a 
                    href={contact.social.instagram} 
                    className="w-12 h-12 bg-gray-800 rounded-full flex items-center justify-center hover:bg-lime-400 hover:text-black transition-colors"
                    aria-label="Instagram"
                  >
                    <Instagram size={20} />
                  </a>
                  <a 
                    href={contact.social.youtube} 
                    className="w-12 h-12 bg-gray-800 rounded-full flex items-center justify-center hover:bg-lime-400 hover:text-black transition-colors"
                    aria-label="YouTube"
                  >
                    <Youtube size={20} />
                  </a>
                  <a 
                    href={contact.social.twitter} 
                    className="w-12 h-12 bg-gray-800 rounded-full flex items-center justify-center hover:bg-lime-400 hover:text-black transition-colors"
                    aria-label="Twitter"
                  >
                    <Twitter size={20} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;